// Modules
import styled from "styled-components"
import { rgba } from "polished"

// Constants
import colors from "~constants/colors"
import React from "react"

interface ButtonProps extends React.ButtonHTMLAttributes<HTMLButtonElement> {
  width?: string
  height?: string
  borderRadius?: string
  color?: string
  fontSize?: number
}

const ButtonNormalBase = styled.button`
  border-radius: 6px;
  border: none;
  cursor: pointer;
  font-weight: bold;
  max-height: 40px;
  outline: none;

  &[disabled] {
    cursor: not-allowed;
    opacity: 0.5;
  }
`

export const ButtonIcon = styled(ButtonNormalBase)<ButtonProps>`
  display: flex;
  background-color: ${props => props.color || colors.gray10};
  width: ${props => props.width || 44}px;
  height: ${props => props.height || 44}px;
  border-radius: ${props => props.borderRadius || "auto"};
  align-items: center;
  justify-content: center;

  &:focus-visible {
    outline: 2px solid ${colors.blue1};
  }

  > svg {
    color: ${props => props.color || ""};
  }
`

export const ButtonGroup = styled.div`
  align-items: center;
  display: flex;

  > *:not(:last-child) {
    margin-right: 5px;
  }

  > .link-button {
    display: inherit;
  }
`

export const ButtonContainer = styled.span`
  align-items: center;
  display: inline-flex;

  > button {
    margin-right: 15px;
  }
`

export const ButtonPrimary = styled(ButtonNormalBase)`
  background-color: ${colors.blue1};
  color: white;
  min-width: 80px;
  height: 40px;
  padding: 0 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;

  &.loading {
    > .loading-icon {
      color: white;
    }
  }
`

export const createButtonStyle: React.CSSProperties = {
  marginBottom: "30px",
  textAlign: "right",
}

export const ButtonPrimarySoftLarge = styled(ButtonNormalBase)`
  align-items: center;
  background-color: ${rgba(colors.blue2, 0.1)};
  color: ${colors.blue1};
  display: inline-flex;
  height: 50px;
  min-width: 80px;
  padding: 0 15px;

  &:focus-visible {
    outline: 0;
    box-shadow: 0 0 0 2px ${colors.blue1};
  }

  > svg {
    height: 32px;
    margin-right: 10px;
    width: auto;
  }
`

export const ButtonSecondarySoftLarge = styled(ButtonNormalBase)`
  align-items: center;
  background-color: ${rgba(colors.gray2, 0.4)};
  color: ${colors.cerulean};
  display: inline-flex;
  height: 50px;
  min-width: 80px;
  padding: 0 15px;

  > svg {
    height: 32px;
    margin-right: 10px;
    width: auto;
  }
`

export const ButtonSecondary = styled(ButtonNormalBase)`
  background-color: ${colors.gray2};
  color: ${colors.cerulean};
  min-width: 80px;
  height: 40px;
  padding: 0 15px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
`

export const ButtonForm = styled(ButtonNormalBase)<ButtonProps>`
  background-color: white;
  border-radius: 4px;
  border: 0px solid ${colors.cerulean};
  color: ${props => props.color || colors.cerulean};
  display: inline-block;
  font-size: ${props => props.fontSize || 12}px;
  font-weight: normal;
  height: 30px;
  line-height: 30px;
  padding: 0 10px;
  min-width: 80px;

  &.active {
    background-color: ${colors.cerulean};
    color: white;
  }

  &.green {
    background-color: ${colors.green};
    color: ${colors.white};
  }
`

export const ButtonFormSelection = styled(ButtonNormalBase)`
  background-color: white;
  border-radius: 4px;
  border: 1px solid ${colors.cerulean};
  color: ${colors.cerulean};
  display: inline-block;
  font-size: 12px;
  font-weight: normal;
  height: 30px;
  line-height: 30px;
  padding: 0 10px;
  min-width: 80px;

  &.active {
    background-color: ${colors.cerulean};
    color: white;
  }
`

export const ButtonFormContainer = styled.div`
  margin-top: 5px;
`
